<template>
  <v-container 
    v-if="sessions.length"
    class="
      d-flex flex-column gap-2
      px-0
    "
  >
    <template v-for="(filter, index) in filters">
      <label v-if="filter.type === 'select'" :key="`${filter.key}-select`" class="d-flex flex-column gap-1">
        <span class="text--secondary caption font-weight-bold">{{ filter.label }}</span>
        <v-select
          :value="activeFilters[filter.key]"
          :items="filter.items"
          v-bind="filter.itemText ? { 'item-text': filter.itemText, 'item-value': filter.itemValue } : {}"
          outlined
          dense
          hide-details
          color="third"
          item-color="third"
          class="rounded-lg body-2"
          @input="updateFilter(filter.key, $event)"
        />
      </label>
      <template v-else-if="filter.type === 'checkbox'" >
        <p v-if="index === filters.findIndex(f => f.type === 'checkbox')" class="text--secondary caption font-weight-bold mb-0">Filtres intelligents</p>
        <section class="d-flex align-center gap-1">
          <v-checkbox
            :id="`${filter.key}-checkbox`"
            :input-value="activeFilters[filter.key]"
            :key="`${filter.key}-checkbox`"
            dense
            hide-details
            color="third"
            class="mt-0 pt-0"
            :ripple="false"
            @change="updateFilter(filter.key, $event)"
          />
          <label :for="`${filter.key}-checkbox`" class="caption font-weight-bold pointer">{{ filter.label }}</label>
        </section>
      </template>
    </template>
  </v-container>
</template>

<script>
import { ref, computed, onBeforeMount } from '@vue/composition-api';
import store from '@/store';

export default {
  name: 'SessionsFiltersPanel',

  props: {
    sessions: {
      type: Array,
      required: true
    }
  },
  setup (props) {
    const disciplines = computed(() => store.state.disciplines);
    const products = computed(() => store.state.products);
    const instructors = computed(() => store.state.instructors);

    const sessionTypesList = computed(() => {
      const allTypes = Object.entries(store.state.references?.equestrian_session_values || {}).map(([key, value]) => ({
        value: key,
        label: value
      }));
      return [
        { label: 'Tous', value: null },
        ...allTypes.filter(s => !store.state['sessionsFilters'].repriseTabSessionTypesToExclude.includes(s.value) && props.sessions.some(session => session.type === s.value))
      ];
    });

    const disciplinesList = computed(() => {
      return [
        { name: 'Toutes', uuid: null },
        ...disciplines.value.filter(d => props.sessions.some(session => session.discipline?.uuid === d.uuid))
      ];
    });

    const productsList = computed(() => {
      return [
        { wtitle: 'Tous', uuid: null },
        ...products.value.filter(p => props.sessions.some(session => (session.product_activity || []).some(pa => pa.product === p.uuid)))
      ];
    });

    const instructorsList = computed(() => {
      return [
        { fullname: 'Tous', uuid: null },
        ...instructors.value.filter(i => props.sessions.some(session => session.instructors?.some(instructor => instructor.uuid === i.uuid)))
      ];
    });

    const activitiesList = computed(() => {
      const activities = [];
      (productsList.value || []).forEach(product => {
        (product.activities || []).forEach(activity => {
          if (!activities.some(a => a.uuid === activity.uuid)) {
            activities.push(activity);
          }
        });
      });
      return [
        { activity_name: 'Toutes', uuid: null },
        ...activities
      ];
    });

    const levelsList = computed(() => {
      const levels = [];
      store.state.algoliaCustomers.forEach(customer => {
        if (customer.equestrian_level && !levels.some(l => l.value === customer.equestrian_level)) {
          levels.push({ label: customer.equestrian_level, value: customer.equestrian_level });
        }
      });
      return [
        { label: 'Tous', value: null },
        ...levels
      ];
    });

    const timePeriodsList = computed(() => ([
      { label: 'Toute la journée', value: null },
      { label: 'Matin (8h-12h)', value: 'morning' },
      { label: 'Après-midi (12h-18h)', value: 'afternoon' },
      { label: 'Soir (18h-22h)', value: 'evening' }
    ]));

    const filters = computed(() => ([
      { key: 'sessionType', type: 'select', label: 'Type de session', items: sessionTypesList.value, itemText: 'label', itemValue: 'value' },
      { key: 'discipline', type: 'select', label: 'Discipline', items: disciplinesList.value, itemText: 'name', itemValue: 'uuid' },
      { key: 'product', type: 'select', label: 'Produit', items: productsList.value, itemText: 'wtitle', itemValue: 'uuid' },
      { key: 'activity', type: 'select', label: 'Activité', items: activitiesList.value, itemText: 'activity_name', itemValue: 'uuid' },
      { key: 'level', type: 'select', label: 'Niveau', items: levelsList.value, itemText: 'label', itemValue: 'value' },
      { key: 'instructor', type: 'select', label: 'Moniteur', items: instructorsList.value, itemText: 'fullname', itemValue: 'uuid' },
      { key: 'timePeriod', type: 'select', label: "Période horaire", items: timePeriodsList.value, itemText: 'label', itemValue: 'value' },
      { key: 'riderOverload', type: 'checkbox', label: "Reprises en tension" },
      { key: 'completeSessions', type: 'checkbox', label: 'Reprises complètes' },
      { key: 'overbookedSessions', type: 'checkbox', label: 'Reprises surbookées' }
    ]));

    const activeFilters = computed(() => store.state.sessionsFilters?.active || {});

    const updateFilter = (key, value) => {
      store.commit('sessionsFilters/setFilters', { key, value });
    };

    onBeforeMount(async () => {
      if (!store.state.isLazyLoaded.instructors) {
        await store.dispatch('getInstructors');
      }
      if (!store.state.isLazyLoaded.products) {
        await store.dispatch('getProducts');
      }
    });

    return {
      filters,
      activeFilters,
      updateFilter
    };
  }
};
</script>
